<script setup lang="ts">
import DeleteButton from "@/components/DeleteButton.vue";
import EditLink from "@/components/links/EditLink.vue";
import { formatAddress } from "@/contacts/formatAddress";
import {
  Mail,
  MapPin,
  Phone,
  Trash,
  User,
  UserCrown,
  UserStar,
} from "@iconoir/vue";
import { useDeleteContact } from "./contactsCrud";
import type { ContactListItem } from "./types";

defineProps<{
  contacts: Array<ContactListItem>;
}>();

const { deleteContact, resetDeleteContact, deleteContactStatus } =
  useDeleteContact();

function formatName(contact: ContactListItem): string {
  return contact.salutation != null
    ? `${contact.salutation} ${contact.name}`
    : contact.name;
}
</script>
<template>
  <table>
    <thead>
      <tr>
        <th><span class="visually-hidden">Status</span></th>
        <th>Name</th>
        <th><MapPin /> Adresse</th>
        <th><Mail /> E-Mail</th>
        <th><Phone /> Telefon</th>
        <th><User /> angelegt von</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="contact in contacts" :key="contact.id">
        <td>
          <span
            v-if="contact.isHouseSpeaker"
            role="img"
            title="Haussprecher:in"
            aria-label="Haussprecher:in"
            ><UserCrown color="var(--icon-color-yellow)"
          /></span>
          <span
            v-else-if="contact.isPotentialCoreActivist"
            role="img"
            title="potentielle:r Kernaktive:r"
            aria-label="potentielle:r Kernaktive:r"
            ><UserStar color="var(--icon-color-green)"
          /></span>
          <span
            v-else-if="contact.hasNoInterestInBecomingActive"
            role="img"
            title="kein Interesse"
            aria-label="kein Interesse"
            ><User color="var(--icon-color-red)"
          /></span>
        </td>
        <td>
          <RouterLink
            :to="{
              name: 'editContact',
              params: { id: contact.id },
            }"
            class="column--primary-action"
          >
            {{ formatName(contact) }}
          </RouterLink>
        </td>
        <td>
          {{ formatAddress(contact.address, contact.apartment) }}
        </td>
        <td style="overflow-wrap: anywhere">
          <template v-if="contact.emailAddressPendingConfirmation === true">
            Bestätigung ausstehend...
          </template>
          <template v-else>{{ contact.emailAddress }}</template>
        </td>
        <td>
          <template v-if="contact.phoneNumberPendingConfirmation === true">
            Bestätigung ausstehend...
          </template>
          <template v-else>{{ contact.phoneNumber }}</template>
        </td>
        <td>{{ contact.createdByUsername ?? "n/a" }}</td>
        <td class="actions">
          <EditLink :to="{ name: 'editContact', params: { id: contact.id } }" />
          <DeleteButton
            class="button button--tertiary"
            :deleting-status="deleteContactStatus"
            @initiated="resetDeleteContact()"
            @confirmed="deleteContact(contact)"
          >
            <Trash />
            <span class="visually-hidden"> Löschen </span>
            <template #confirmation>
              Möchtest du den Kontakt "{{ contact.name }}" wirklich löschen?
            </template>
          </DeleteButton>
        </td>
      </tr>
    </tbody>
  </table>
</template>
